// Gallery.jsx
// Page displaying portfolio of work

import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/Gallery.css';

function Gallery() {
  const { t } = useLanguage();

  // Array of gradient colors for placeholder images
  // In real app, these would be actual image URLs
  const gradients = [
    'linear-gradient(135deg, #C19A6B, #8B4513)',
    'linear-gradient(135deg, #DAA520, #C19A6B)',
    'linear-gradient(135deg, #8B4513, #654321)',
    'linear-gradient(135deg, #D4AF37, #DAA520)',
    'linear-gradient(135deg, #C19A6B, #DAA520)',
    'linear-gradient(135deg, #8B4513, #C19A6B)',
  ];

  // Array of icons for each gallery item
  const icons = ['👗', '👘', '💃', '👔', '👗', '✨'];

  return (
    <div className="gallery-page">
      <section className="gallery">
        {/* Page Header */}
        <h2 className="section-title">{t.gallery.title}</h2>
        <p className="section-subtitle">{t.gallery.subtitle}</p>
        
        {/* Gallery Grid */}
        <div className="gallery-grid">
          {/*
            ARRAY METHODS CHAINING:
            We can chain array methods together:
            - t.gallery.samples is an array
            - .map() creates a new element for each item
            
            (sample, index) => 
            - sample: the current item ("Designer Blouse\nSample 1")
            - index: the position in array (0, 1, 2, 3, 4, 5)
          */}
          {t.gallery.samples.map((sample, index) => (
            <div key={index} className="gallery-item">
              {/*
                INLINE STYLES IN REACT:
                In HTML: style="background: red;"
                In React: style={{ background: 'red' }}
                
                Double curly braces because:
                - Outer {} = This is JavaScript
                - Inner {} = This is an object
                
                We use gradients[index] to get the color for this item
              */}
              <div 
                className="gallery-placeholder" 
                style={{ background: gradients[index] }}
              >
                <div>
                  <div className="gallery-icon">{icons[index]}</div>
                  {/*
                    WHITESPACE IN JSX:
                    \n in the string creates a line break
                    We preserve it by using whiteSpace: 'pre-line'
                  */}
                  <div style={{ whiteSpace: 'pre-line' }}>{sample}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="gallery-disclaimer">
          <p>{t.gallery.disclaimer}</p>
        </div>
      </section>
    </div>
  );
}

export default Gallery;

/*
REACT STYLING - THREE APPROACHES:

1. EXTERNAL CSS (What we're using):
   - Create .css file
   - Import it: import './Gallery.css'
   - Use className: <div className="gallery-grid">
   - Best for: Most styling needs

2. INLINE STYLES (Used for dynamic backgrounds):
   - Use style prop with object
   - <div style={{ background: 'red', color: 'white' }}>
   - Best for: Dynamic styles that change based on data

3. CSS-IN-JS (Libraries like styled-components):
   - Write CSS in JavaScript
   - Best for: Large apps with complex theming
   - We're not using this in this project

WHEN TO USE INLINE STYLES:
- Styles that change based on data (like our gradients)
- Dynamic colors, positions, sizes
- Keep it minimal - most styling should be in CSS files
*/

/*
UNDERSTANDING ARRAY INDEXING:

const gradients = ['color1', 'color2', 'color3'];

gradients[0] = 'color1'  (first item)
gradients[1] = 'color2'  (second item)
gradients[2] = 'color3'  (third item)

When we map with index:
samples.map((sample, index) => ...)

index goes: 0, 1, 2, 3, 4, 5
So gradients[index] gives us a different gradient for each item!

This is how we create variety in our gallery items.
*/
